// Port of src/app/components/nav-bar/file-upload/file-upload.component.{ts,html}
// (mypart-file-upload). The Angular .scss file was empty — no stylesheet.
//
// Mapping notes:
// - NavBarService signals → useNavBarStore (resetFileState, uploadProgress via
//   setNavBarField, uploadStarted, image/videoPreview, isImageFile).
// - The Angular createPopupSignal → createPopUp() flow built an imperative tippy
//   popup on the thumbnail showing a 320px preview on hover; here it is rendered
//   declaratively via a hover Tooltip wrapping the thumbnail (placement bottom).
// - [tp]="isMobileDevice() ? null : 'Search by image/video'" → Tooltip with
//   disabled={isMobileDevice}.
// - (outsideClick) on the mobile options menu → useOutsideClick.
// - NOTE(port): the Angular component also carried takePhoto()/recordVideo()
//   (MediaDevices camera overlays) and fallbackToFileInput() — none of them is
//   reachable from the template (the menu buttons call selectFromGallery/
//   capturePhoto/captureVideo). They are dead code and are not ported.
// - The user field / showSignUpPopupEvent output exist in Angular only inside
//   commented-out guards; the onShowSignUpPopup prop is kept for the NavBar
//   wiring parity but is never invoked (same as Angular).

import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, MouseEvent } from 'react';
import Tooltip from '../../shared/components/Tooltip';
import { useNavBarStore } from '../../stores/navbar-store';
import { useDeviceStore } from '../../stores/device-store';
import { useToastStore } from '../../stores/toast-store';
import { useOutsideClick } from '../../shared/hooks/useOutsideClick';

// Maximum file sizes in bytes
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB

export interface FileUploadProps {
  onChangeFile: (file: File) => void;
  /** Never emitted (the Angular guard is commented out) — kept for parity. */
  onShowSignUpPopup?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function FileUpload({ onChangeFile, onShowSignUpPopup }: FileUploadProps) {
  const isMobileDevice = useDeviceStore(s => s.isMobileDevice);
  const uploadStarted = useNavBarStore(s => s.uploadStarted);
  const uploadProgress = useNavBarStore(s => s.uploadProgress);
  const imagePreview = useNavBarStore(s => s.imagePreview);
  const videoPreview = useNavBarStore(s => s.videoPreview);
  const isImageFile = useNavBarStore(s => s.isImageFile);

  const [showUploadOptions, setShowUploadOptions] = useState(false);

  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const optionsMenuRef = useOutsideClick<HTMLDivElement>(() => setShowUploadOptions(false));

  // Cleanup the progress interval on unmount.
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  function onFileChange(event: ChangeEvent<HTMLInputElement> | { target: HTMLInputElement }): void {
    const toast = useToastStore.getState();
    const fileInput = event.target;

    // Check if files exist and handle mobile device issues
    if (fileInput.files && fileInput.files.length > 0) {
      useNavBarStore.getState().resetFileState();

      // Get the file and check it's valid
      const file = fileInput.files[0];

      // Log file details for debugging
      console.log('File selected:', file.name, file.type, file.size);

      // Mobile devices sometimes return empty files or files with zero size
      if (!file || file.size === 0) {
        console.error('File is empty or invalid');
        toast.error('The file selection failed. Please try again.');
        fileInput.value = '';
        return;
      }

      // Check file size limits
      if (file.type.startsWith('image/') && file.size > MAX_IMAGE_SIZE) {
        toast.error(`Image file is too large. Maximum size is ${MAX_IMAGE_SIZE / (1024 * 1024)}MB.`);
        fileInput.value = '';
        return;
      }

      if (file.type.startsWith('video/') && file.size > MAX_VIDEO_SIZE) {
        toast.error(`Video file is too large. Maximum size is ${MAX_VIDEO_SIZE / (1024 * 1024)}MB.`);
        fileInput.value = '';
        return;
      }

      // Reset upload progress
      useNavBarStore.getState().setNavBarField('uploadProgress', 0);

      // Simulate upload progress (in a real app, this would be based on actual upload progress)
      simulateUploadProgress();

      onChangeFile(file);

      // Reset the file input after handling the file
      fileInput.value = ''; // Clear the file input field

      // Hide upload options menu if it was open
      setShowUploadOptions(false);
    } else {
      console.error('No files received from input');
      toast.warning('No file was selected. Please try again.');
    }
  }

  function fileUploadClicked(event: MouseEvent<HTMLInputElement>) {
    // For mobile devices, show options menu instead of directly opening file picker
    if (useDeviceStore.getState().isMobileDevice) {
      event.preventDefault();
      setShowUploadOptions(prev => !prev);
    }
  }

  // Generic temp-input flow shared by the three menu options (the Angular
  // methods selectFromGallery/capturePhoto/captureVideo were copy-pastes of
  // this exact sequence with different accept/capture values).
  function openTempFileInput(accept: string, capture?: string): void {
    setShowUploadOptions(false);

    // Create a temporary file input to bypass the event prevention
    const tempFileInput = document.createElement('input');
    tempFileInput.type = 'file';
    tempFileInput.accept = accept;
    if (capture) {
      tempFileInput.capture = capture; // Use the environment-facing camera
    }
    tempFileInput.style.display = 'none';
    document.body.appendChild(tempFileInput);

    // Add change event listener to handle file selection
    tempFileInput.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        // Process the selected file using the existing onFileChange logic
        onFileChange({ target });
      }
      // Clean up the temporary input
      document.body.removeChild(tempFileInput);
    });

    // Trigger the file selection dialog
    tempFileInput.click();
  }

  // Method to select from gallery (no capture)
  const selectFromGallery = () => openTempFileInput('image/*,video/*');

  // Method to capture a photo using native device camera
  const capturePhoto = () => openTempFileInput('image/*', 'environment');

  // Method to capture a video using native device camera
  const captureVideo = () => openTempFileInput('video/*', 'environment');

  // Simulate upload progress (in a real app, this would be based on actual upload progress)
  function simulateUploadProgress(): void {
    let progress = 0;
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    progressIntervalRef.current = setInterval(() => {
      progress += 5;
      useNavBarStore.getState().setNavBarField('uploadProgress', progress);
      if (progress >= 100) {
        clearInterval(progressIntervalRef.current!);
        progressIntervalRef.current = undefined;
      }
    }, 200);
  }

  return (
    <>
      <input onChange={onFileChange} onClick={fileUploadClicked} disabled={uploadStarted}
             accept="image/*,video/*"
             capture="environment"
             className="hidden"
             id="file-upload"
             type="file" />
      <Tooltip disabled={isMobileDevice} content="Search by image/video">
        <label className={`flex items-center cursor-pointer${uploadStarted ? ' !cursor-default' : ''}`}
               htmlFor="file-upload">
          {imagePreview ? (
            <Tooltip
                placement="bottom"
                content={
                  isImageFile ? (
                    <img src={imagePreview} alt="Preview" className="w-[320px] h-auto" />
                  ) : (
                    <video src={videoPreview} className="w-[320px] h-auto" controls></video>
                  )
                }>
              <img src={imagePreview} alt="File Preview" className="w-6 h-6" />
            </Tooltip>
          ) : (
            <svg aria-hidden="true" className="w-6 h-6 text-[#c42727]" fill="currentColor"
                 viewBox="0 0 20 18" xmlns="http://www.w3.org/2000/svg">
              <path
                  d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
            </svg>
          )}
        </label>
      </Tooltip>

      {/* Upload options menu for mobile devices */}
      {isMobileDevice && showUploadOptions && (
        <div ref={optionsMenuRef}
             className="upload-options-menu absolute top-full right-0 mt-2 bg-white dark:bg-zinc-800 rounded-lg shadow-lg z-50 w-48 transition-all duration-200 ease">
          {/* Gallery option */}
          <button onClick={selectFromGallery}
                  className="w-full text-left py-2 px-3 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white rounded flex items-center"
                  type="button">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    strokeLinecap="round" strokeLinejoin="round"
                    strokeWidth="2"></path>
            </svg>
            Gallery
          </button>
          {/* Take Photo option */}
          <button onClick={capturePhoto}
                  className="w-full text-left py-2 px-3 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white rounded flex items-center"
                  type="button">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    strokeLinecap="round" strokeLinejoin="round"
                    strokeWidth="2"></path>
              <path d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round"
                    strokeWidth="2"></path>
            </svg>
            Take Photo
          </button>
          {/* Record Video option */}
          <button onClick={captureVideo}
                  className="w-full text-left py-2 px-3 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white rounded flex items-center"
                  type="button">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    strokeLinecap="round" strokeLinejoin="round"
                    strokeWidth="2"></path>
            </svg>
            Record Video
          </button>
        </div>
      )}

      {/* Upload progress indicator */}
      {uploadStarted && uploadProgress < 100 && (
        <div className="upload-progress absolute bottom-[-20px] left-0 w-full h-1 bg-gray-200 dark:bg-gray-700
               mb-4 rounded-full overflow-hidden">
          <div style={{ width: `${uploadProgress}%` }}
               className="h-full bg-gradient-to-r from-gray-700 to-gray-100 transition-all"></div>
        </div>
      )}
    </>
  );
}
